/**
 * API Helper with CSRF Token Management
 * Centralized fetch wrapper that automatically handles CSRF tokens
 */

let csrfToken = null;

/**
 * Fetch CSRF token from server
 * @returns {Promise<string>} The CSRF token
 */
async function fetchCSRFToken() {
    try {
        const response = await fetch('/api/csrf-token', {
            credentials: 'include'
        });
        if (response.ok) {
            const data = await response.json();
            csrfToken = data.csrfToken;
            return csrfToken;
        }
    } catch (error) {
        console.error('Failed to fetch CSRF token:', error);
    }
    return null;
}

/**
 * Get current CSRF token, fetching if not available
 * @returns {Promise<string>} The CSRF token
 */
async function getCSRFToken() {
    if (!csrfToken) {
        await fetchCSRFToken();
    }
    return csrfToken;
}

/**
 * Initialize CSRF token (call on app start)
 * @returns {Promise<void>}
 */
export async function initCSRF() {
    await fetchCSRFToken();
    console.log('✅ CSRF token initialized');
}

/**
 * Wrapper around fetch that automatically adds CSRF token for state-changing requests
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export async function apiFetch(url, options = {}) {
    const method = (options.method || 'GET').toUpperCase();
    
    // Add credentials by default
    options.credentials = options.credentials || 'include';
    
    // For state-changing methods, add CSRF token
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        const token = await getCSRFToken();
        
        if (token) {
            options.headers = {
                ...options.headers,
                'x-csrf-token': token
            };
        }
    }
    
    const response = await fetch(url, options);
    
    // If we get a 403 with CSRF error, try refreshing the token and retry once
    if (response.status === 403) {
        try {
            const errorData = await response.clone().json();
            if (errorData.error && errorData.error.includes('CSRF')) {
                console.log('🔄 CSRF token expired, refreshing...');
                await fetchCSRFToken();
                
                // Retry with new token
                if (csrfToken) {
                    options.headers = {
                        ...options.headers,
                        'x-csrf-token': csrfToken
                    };
                    return await fetch(url, options);
                }
            }
        } catch (e) {
            // Not JSON or other error, return original response
        }
    }
    
    return response;
}

/**
 * Convenience method for GET requests
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiGet(url, options = {}) {
    return apiFetch(url, { ...options, method: 'GET' });
}

/**
 * Convenience method for POST requests
 * @param {string} url - The URL to fetch
 * @param {object} body - The request body (will be JSON stringified)
 * @param {RequestInit} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiPost(url, body, options = {}) {
    return apiFetch(url, {
        ...options,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        },
        body: JSON.stringify(body)
    });
}

/**
 * Convenience method for PUT requests
 * @param {string} url - The URL to fetch
 * @param {object} body - The request body (will be JSON stringified)
 * @param {RequestInit} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiPut(url, body, options = {}) {
    return apiFetch(url, {
        ...options,
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        },
        body: JSON.stringify(body)
    });
}

/**
 * Convenience method for DELETE requests
 * @param {string} url - The URL to fetch
 * @param {object} body - Optional request body (will be JSON stringified)
 * @param {RequestInit} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiDelete(url, body = null, options = {}) {
    const fetchOptions = {
        ...options,
        method: 'DELETE'
    };
    
    if (body) {
        fetchOptions.headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };
        fetchOptions.body = JSON.stringify(body);
    }
    
    return apiFetch(url, fetchOptions);
}
